import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { TOAST_CONFIG, ToastConfig } from './toast-config.model';
import { ToastService } from './toast.service';
import { ErrorMessageService } from '../../shared/utils/http-error-responses/error-message.service';

@Injectable()
export class ToastInterceptor implements HttpInterceptor {
  constructor(
    private toastService: ToastService,
    private errorMessageService: ErrorMessageService
  ) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const cfg: ToastConfig | undefined = req.context.get(TOAST_CONFIG);

    return next.handle(req).pipe(
      tap({
        next: (ev) => {
          if (!(ev instanceof HttpResponse)) {
            return;
          }

          const isMutating: boolean = /^(POST|PUT|PATCH|DELETE)$/.test(req.method);
          const shouldSuccess: boolean = cfg?.success ?? isMutating;
          if (!shouldSuccess) {
            return;
          }

          const msg: string = resolveSuccessMessage(cfg, req.method);
          this.toastService.show({ level: 'success', title: 'Success', message: msg });
        },
        error: (error) => {
          const err: HttpErrorResponse = error as HttpErrorResponse;
          const shouldError: boolean = cfg?.error ?? defaultShouldErrorToast(err);
          if (!shouldError) {
            return;
          }

          const messages: string[] = this.errorMessageService.buildErrorMessages(err);
          for (const message of messages) {
            this.toastService.show({ level: 'error', title: 'Error', message: message });
          }
        },
      })
    );
  }
}

/** Prefer a caller-provided message; otherwise use a tiny verb-based default. */
function resolveSuccessMessage(cfg: ToastConfig | undefined, method: string): string {
  const sm: { key?: string; text?: string; params?: Record<string, unknown> } | undefined =
    cfg?.successMessage;
  if (sm?.text) {
    return interpolate(sm.text, sm.params);
  }

  switch (method) {
    case 'POST':
      return 'Created.';
    case 'PUT':
    case 'PATCH':
      return 'Saved.';
    case 'DELETE':
      return 'Deleted.';
    default:
      return 'Success.';
  }
}

/** Keep error toast logic tiny and predictable. */
function defaultShouldErrorToast(err: HttpErrorResponse): boolean {
  return (
    err.status === 0 ||
    err.status >= 500 ||
    err.status === 400 ||
    err.status === 401 ||
    err.status === 403 ||
    err.status === 409
  );
}

/** Minimal string interpolation for {{placeholders}}. */
function interpolate(text: string, params?: Record<string, unknown>): string {
  if (!params) {
    return text;
  }
  return text.replace(/\{\{(\w+)\}\}/g, (_, key) => String(params[key] ?? ''));
}
